import React, { useCallback, useEffect, useRef, useState } from "react"

import { useAccount } from "../../../../hooks/useAccount"
import { useAnalytics } from "../../../../analytics/provider"
import { useApp } from "../../../../hooks/useApp"
import { usePackages } from "../../../../hooks/usePackages"
import { useTemplate } from "../../../../hooks/useTemplate"
import { useSearch } from "../../../../providers/search"

export const withHeroForm = Component => ({ name = "HeroForm" }) => {
  const { profile } = useAccount()
  const { trackSearchEvent } = useAnalytics()
  const { config } = useApp()
  const { packages } = usePackages()
  const { queryContext, loading, search } = useSearch()
  const input = useRef()
  const { global: locales } = useTemplate()

  const googleMapsApiKey = React.useMemo(() => config.services.googleMaps.key, [config])

  const autocompleteConfig = React.useMemo(
    () => ({
      apiKey: googleMapsApiKey,
      minLengthAutocomplete: 3,
      autocompletionRequest: {
        componentRestrictions: {
          country: ["au"],
        },
        types: ["(regions)"],
      },
    }),
    [googleMapsApiKey]
  )

  const [searchInput, setSearchInput] = useState({
    suburb: queryContext?.suburb || profile?.careSuburb || "",
    packageLevel: queryContext?.packageLevel?.toString() || profile?.carePackage || packages?.[0]?.number?.toString() || "",
  })
  const [step, setStep] = useState(0)
  const [trackedStart, setTrackedStart] = useState(false)

  const handleChange = useCallback(({ target: { name, value } }) => setSearchInput(prevState => ({ ...prevState, [name]: value })), [setSearchInput])

  const handleAutocomplete = useCallback(
    ({ description: value }) => {
      setSearchInput(prevState => ({ ...prevState, suburb: value }))
      trackSearchEvent({
        group: "provider search home",
        event: "custom.search.field.complete",
        label: "suburb",
        info: {
          element: "suburb",
        },
        data: {
          value,
        },
      })
    },
    [setSearchInput, trackSearchEvent]
  )

  const handleClick = useCallback(() => {
    if (!trackedStart) {
      trackSearchEvent({
        group: "provider search home",
        event: "custom.search.start",
        label: undefined,
      })

      setTrackedStart(true)
    }
  }, [setTrackedStart, trackedStart, trackSearchEvent])

  const handleBlur = useCallback(
    event => {
      trackSearchEvent({
        group: "provider search home",
        event: "custom.search.field.complete",
        label: event.target.name,
        info: {
          element: event.target.name,
        },
        data: {
          value: event.target.value,
        },
      })
    },
    [trackSearchEvent]
  )

  const updateSuburb = (event, suggestion, onSelectSuggestion) => {
    onSelectSuggestion(suggestion, event)
    setSearchInput(prevState => ({ ...prevState, suburb: suggestion.description }))
  }

  const handleStep = (step: number) => setStep(step)

  const handleSubmit = (event: any) => {
    event.preventDefault()

    if (!step) {
      handleStep(1)
    } else {
      submit()
    }
  }

  const submit = useCallback(async () => {
    if (!searchInput.suburb || !searchInput.packageLevel) return

    trackSearchEvent({
      group: "provider search home",
      event: "custom.search.submit",
      label: undefined,
      data: undefined,
      search: {
        suburb: searchInput?.suburb,
        package: searchInput?.packageLevel,
      },
      filters: {},
    })

    search({
      suburb: searchInput.suburb,
      packageLevel: searchInput.packageLevel,
    })
  }, [searchInput, search, trackSearchEvent])

  useEffect(() => {
    if (input?.current) setTimeout(() => input?.current?.focus(), 50)
  }, [step])

  Component.displayName = name
  return (
    <Component
      autocompleteConfig={autocompleteConfig}
      handleAutocomplete={handleAutocomplete}
      handleBlur={handleBlur}
      handleChange={handleChange}
      handleClick={handleClick}
      handleStep={handleStep}
      handleSubmit={handleSubmit}
      input={input}
      loading={loading}
      locales={locales}
      packages={packages}
      searchInput={searchInput}
      step={step}
      updateSuburb={updateSuburb}
    />
  )
}
